import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Search,
  LogOut,
  Bell,
} from 'lucide-react'
import type { User } from '@/api/types'
import { NAV_ACTIONS } from '@/components/command-centre/navigation-map'
import { cn } from '@/lib/utils'

interface FinderBarProps {
  user?: User | null
  activeTab: string
  onSwitchTab: (tabId: string) => void
  onOpenWindow?: (route: string, title: string) => void
  openWindowTitles?: string[]
  onCloseAllWindows?: () => void
  onSwitchToModern?: () => void
  onLogout?: () => void
}

interface MenuItem {
  label: string
  action?: () => void
  disabled?: boolean
  separator?: boolean
}

const CC_TAB_LABELS: Record<string, string> = {
  accounts: 'Accounts',
  banking: 'Banking',
  sales: 'Sales',
  'time-billing': 'Time Billing',
  purchases: 'Purchases',
  inventory: 'Inventory',
  'card-file': 'Card File',
}

export function FinderBar({
  user,
  activeTab: _activeTab,
  onSwitchTab,
  onOpenWindow,
  openWindowTitles = [],
  onCloseAllWindows,
  onSwitchToModern,
  onLogout,
}: FinderBarProps) {
  const navigate = useNavigate()
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const [menuLeft, setMenuLeft] = useState(0)
  const [showPalette, setShowPalette] = useState(false)
  const [paletteQuery, setPaletteQuery] = useState('')
  const paletteInputRef = useRef<HTMLInputElement>(null)
  const menuBarRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const menuBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({})

  const handleMenuOpen = useCallback((label: string) => {
    const btn = menuBtnRefs.current[label]
    if (btn && menuBarRef.current) {
      setMenuLeft(btn.offsetLeft)
    }
    setOpenMenu(prev => prev === label ? null : label)
  }, [])

  const handleMenuHover = useCallback((label: string) => {
    if (!openMenu) return
    const btn = menuBtnRefs.current[label]
    if (btn && menuBarRef.current) {
      setMenuLeft(btn.offsetLeft)
    }
    setOpenMenu(label)
  }, [openMenu])

  // Close menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        menuBarRef.current && !menuBarRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setOpenMenu(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Cmd+K / Ctrl+K hotkey
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setShowPalette(prev => !prev)
      }
      if (e.key === 'Escape') {
        setShowPalette(false)
        setOpenMenu(null)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    if (showPalette) {
      setPaletteQuery('')
      setTimeout(() => paletteInputRef.current?.focus(), 50)
    }
  }, [showPalette])

  const openInWindow = useCallback((route: string, title: string) => {
    if (onOpenWindow) {
      onOpenWindow(route, title)
    } else {
      navigate(route)
    }
    setOpenMenu(null)
  }, [onOpenWindow, navigate])

  const menus: Record<string, MenuItem[]> = {
    File: [
      { label: 'New Journal Entry', action: () => openInWindow('/journal-entries/new', 'New Journal Entry') },
      { label: 'New Invoice', action: () => openInWindow('/invoices/new', 'New Invoice') },
      { label: 'New Quote', action: () => openInWindow('/quotes/new', 'New Quote') },
      { separator: true, label: '---' },
      { label: 'Switch to Modern', action: () => onSwitchToModern?.() },
    ],
    Lists: [
      { label: 'Accounts', action: () => openInWindow('/accounts', 'Accounts') },
      { label: 'Contacts', action: () => openInWindow('/contacts', 'Contacts') },
      { label: 'Jobs', action: () => openInWindow('/jobs', 'Jobs') },
      { label: 'Invoices', action: () => openInWindow('/invoices', 'Invoices') },
      { label: 'Quotes', action: () => openInWindow('/quotes', 'Quotes') },
      { label: 'Purchase Orders', action: () => openInWindow('/purchase-orders', 'Purchase Orders') },
      { label: 'Credit Notes', action: () => openInWindow('/credit-notes', 'Credit Notes') },
      { label: 'Inventory', action: () => openInWindow('/inventory', 'Inventory') },
      { label: 'Time Entries', action: () => openInWindow('/time-tracking', 'Time Entries') },
      { label: 'Expense Claims', action: () => openInWindow('/expense-claims', 'Expense Claims') },
      { label: 'Fixed Assets', action: () => openInWindow('/fixed-assets', 'Fixed Assets') },
    ],
    'Command Centres': Object.entries(CC_TAB_LABELS).map(([id, label]) => ({
      label,
      action: () => { onSwitchTab(id); setOpenMenu(null) },
    })),
    Setup: [
      { label: 'Team', action: () => openInWindow('/team', 'Team') },
      { label: 'Administration', action: () => openInWindow('/admin', 'Administration') },
      { label: 'Billing', action: () => openInWindow('/billing', 'Billing') },
      { separator: true, label: '---' },
      { label: 'Utilities', action: () => openInWindow('/utilities', 'Utilities') },
      { label: 'Bill Comparison', action: () => openInWindow('/bill-comparison', 'Bill Comparison') },
    ],
    Reports: [
      { label: 'View Reports', action: () => openInWindow('/reports', 'Reports') },
    ],
    Window: [
      ...openWindowTitles.map(title => ({
        label: title,
        action: () => setOpenMenu(null),
      })),
      ...(openWindowTitles.length > 0 ? [
        { separator: true, label: '---' },
        { label: 'Close All', action: () => { onCloseAllWindows?.(); setOpenMenu(null) } },
      ] : [
        { label: 'No open windows', disabled: true },
      ]),
    ],
  }

  // Command palette search items
  const paletteItems = Object.values(NAV_ACTIONS)
    .filter(a => a.route !== null)
    .map(a => ({ label: a.label, route: a.route! }))

  const filteredPaletteItems = paletteQuery
    ? paletteItems.filter(i => i.label.toLowerCase().includes(paletteQuery.toLowerCase()))
    : paletteItems

  return (
    <div className="relative z-[10000]">
      {/* Finder bar */}
      <div
        ref={menuBarRef}
        className="flex h-10 items-center justify-between border-b border-border bg-card px-3 select-none"
      >
        {/* Left: menus */}
        <div className="flex items-center gap-0">
          {/* App icon */}
          <button
            onClick={() => setShowPalette(true)}
            className="mr-1.5 flex h-5 w-5 items-center justify-center rounded hover:bg-muted/60"
            title="Command Palette (⌘K)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
              <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
              <path d="M8 10h8" />
              <path d="M8 14h4" />
            </svg>
          </button>

          {Object.entries(menus).map(([label]) => (
            <button
              key={label}
              ref={(el) => { menuBtnRefs.current[label] = el }}
              onClick={() => handleMenuOpen(label)}
              onMouseEnter={() => handleMenuHover(label)}
              className={cn(
                'rounded px-2.5 py-1 text-sm text-foreground/80 transition-colors',
                openMenu === label ? 'bg-primary/15 text-foreground' : 'hover:bg-muted/50',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Right: user + actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPalette(true)}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
            title="Search (⌘K)"
          >
            <Search className="h-4 w-4" />
          </button>
          <button className="rounded p-1 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors">
            <Bell className="h-4 w-4" />
          </button>
          {user && (
            <span className="text-xs text-muted-foreground">{user.name || user.email}</span>
          )}
          {onLogout && (
            <button
              onClick={onLogout}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Dropdown menus */}
      {openMenu && menus[openMenu] && (
        <div ref={dropdownRef} className="absolute left-0 top-10 z-50" style={{ pointerEvents: 'auto' }}>
          <div
            className="w-56 rounded-md border border-border bg-popover py-1.5 shadow-lg"
            style={{ marginLeft: `${menuLeft}px` }}
          >
            {menus[openMenu].map((item, i) =>
              item.separator ? (
                <div key={i} className="my-1 h-px bg-border/50" />
              ) : (
                <button
                  key={i}
                  onClick={item.action}
                  disabled={item.disabled}
                  className={cn(
                    'flex w-full items-center px-3 py-2 text-sm transition-colors',
                    item.disabled
                      ? 'text-muted-foreground/50 cursor-not-allowed'
                      : 'text-popover-foreground hover:bg-accent',
                  )}
                >
                  {item.label}
                </button>
              ),
            )}
          </div>
        </div>
      )}

      {/* Command palette overlay */}
      {showPalette && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={() => setShowPalette(false)}>
          <div className="fixed inset-0 bg-black/30" />
          <div
            className="relative z-10 w-full max-w-md rounded-lg border border-border bg-popover shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                ref={paletteInputRef}
                type="text"
                value={paletteQuery}
                onChange={e => setPaletteQuery(e.target.value)}
                placeholder="Search actions..."
                className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
                onKeyDown={e => {
                  if (e.key === 'Escape') setShowPalette(false)
                }}
              />
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {filteredPaletteItems.length === 0 && (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">No results</p>
              )}
              {filteredPaletteItems.map(item => (
                <button
                  key={item.route + item.label}
                  onClick={() => {
                    openInWindow(item.route, item.label)
                    setShowPalette(false)
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-popover-foreground hover:bg-accent transition-colors"
                >
                  {item.label}
                  <span className="ml-auto text-[10px] text-muted-foreground">{item.route}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
